'use client';

import { useState, useCallback, useMemo } from 'react';
import Select from '@/components/ui/Select';
import { IconInfo } from '@/assets/icons';
import AddressSearchInput from '@/components/ui/AddressSearchInput';
import StreetSearchInput from '@/components/ui/StreetSearchInput';
import BuildingSearchInput from '@/components/ui/BuildingSearchInput';
import RoomSearchInput from '@/components/ui/RoomSearchInput';
import MunicipalityDropdown from '@/components/ui/MunicipalityDropdown';
import { useLanguage } from '@/context/LanguageContext';
import { checkSettlementHasStreets } from '@/utils/addressApi';
import {
  getCpmCategoryOptionsLocalized,
  getCpmSubcategoryOptionsForCategory,
} from '@/lib/calculatorI18n';
import {
  cpmDeductibleOptions,
  cpmPackageOptions,
  cpmRentTypeOptions,
  cpmDepreciationOptions,
} from './constants';
import styles from '../Step1BasicData.module.css';
import AccountEmailInput from '@/components/calculator/AccountEmailInput';

export default function SpecialMachineryFormFields({
  formData,
  setFormData,
  validationErrors,
  showValidationErrors,
  setValidationErrors,
  handlePolicyholderPersonalCodeChange,
  handlePolicyholderAddressChange,
  handleFileChange,
  hidePolicyholder = false,
  /** 'all' | 'machineryIdentity' | 'insurance' — partner form splits sections by plate mode */
  fieldSet = 'all',
}) {
  const { t } = useLanguage();
  const category = formData.cpm_category || '';
  const categoryOptions = useMemo(() => getCpmCategoryOptionsLocalized(t), [t]);
  const subcategoryOptions = useMemo(
    () => getCpmSubcategoryOptionsForCategory(category, t),
    [category, t]
  );
  const deductibleOptions = useMemo(
    () =>
      cpmDeductibleOptions.map(o => ({
        value: o.value,
        label: t(`calculator.stepper_info.cpm_deductible.${o.value}`),
      })),
    [t]
  );
  const packageOptions = useMemo(
    () =>
      cpmPackageOptions.map(o => ({
        value: o.value,
        label: t(`calculator.stepper_info.cpm_package.${o.value}`),
      })),
    [t]
  );
  const rentOptions = useMemo(
    () =>
      cpmRentTypeOptions.map(o => ({
        value: o.value,
        label: t(`calculator.stepper_info.cpm_rent.${o.value}`),
      })),
    [t]
  );
  const depreciationOptions = useMemo(
    () =>
      cpmDepreciationOptions.map(o => ({
        value: o.value,
        label: t(`calculator.stepper_info.cpm_depreciation.${o.value}`),
      })),
    [t]
  );

  const clearError = key => {
    if (validationErrors[key] && setValidationErrors) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const [cpmPackageTooltipOpen, setCpmPackageTooltipOpen] = useState(false);

  const showMachineryIdentity =
    fieldSet === 'all' || fieldSet === 'machineryIdentity';
  const showInsuranceFields = fieldSet === 'all' || fieldSet === 'insurance';

  const handlePolicyholderStreetCheck = useCallback(() => {
    const settlementCode = formData.policyholder?.settlement_code;
    const currentHasStreets = formData.policyholder?.settlementHasStreets;
    if (!settlementCode || currentHasStreets !== undefined) return;

    setFormData(prev => ({
      ...prev,
      policyholder: {
        ...prev.policyholder,
        settlementHasStreets: null,
      },
    }));

    checkSettlementHasStreets(settlementCode)
      .then(hasStreets => {
        setFormData(prev => ({
          ...prev,
          policyholder: {
            ...prev.policyholder,
            settlementHasStreets: hasStreets,
          },
        }));
        if (!hasStreets && setValidationErrors) {
          setValidationErrors(prev => {
            const next = { ...prev };
            delete next.policyholder_street;
            return next;
          });
        }
      })
      .catch(error => {
        console.error(
          '[SpecialMachineryFormFields] Settlement street check failed:',
          error
        );
        setFormData(prev => ({
          ...prev,
          policyholder: {
            ...prev.policyholder,
            settlementHasStreets: true,
          },
        }));
      });
  }, [
    formData.policyholder?.settlement_code,
    formData.policyholder?.settlementHasStreets,
    setFormData,
    setValidationErrors,
  ]);

  return (
    <>
      {!hidePolicyholder ? (
        <>
      {/* Policyholder Section */}
      <div
        className={styles.formGroup}
        style={{ width: '100%', maxWidth: '100%' }}
      >
        <label
          className={styles.label}
          style={{ fontSize: '18px', fontWeight: '500', marginBottom: '20px' }}
        >
          {t('calculator.forms.policyholder_heading')}
        </label>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.health_code_label')}</label>
        <input
          className={`${styles.input} ${validationErrors.policyholder_personal_code ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.health_code_placeholder')}
          value={formData.policyholder?.personal_code || ''}
          autoComplete='username'
          onChange={handlePolicyholderPersonalCodeChange}
          maxLength={11}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_personal_code ? styles.errorHighlight : ''}`}
        >
          {validationErrors.policyholder_personal_code || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.first_name')}</label>
        <input
          className={`${styles.input} ${validationErrors.policyholder_first_name ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.first_name_placeholder')}
          value={formData.policyholder?.first_name || ''}
          autoComplete='given-name'
          onChange={e => {
            setFormData(prev => ({
              ...prev,
              policyholder: {
                ...prev.policyholder,
                first_name: e.target.value,
              },
            }));
          }}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_first_name ? styles.errorHighlight : ''}`}
        >
          {validationErrors.policyholder_first_name || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.last_name')}</label>
        <input
          className={`${styles.input} ${validationErrors.policyholder_last_name ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.last_name_placeholder')}
          value={formData.policyholder?.last_name || ''}
          autoComplete='family-name'
          onChange={e => {
            setFormData(prev => ({
              ...prev,
              policyholder: {
                ...prev.policyholder,
                last_name: e.target.value,
              },
            }));
          }}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_last_name ? styles.errorHighlight : ''}`}
        >
          {validationErrors.policyholder_last_name || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.email')}</label>
        <AccountEmailInput
          className={`${styles.input} ${validationErrors.policyholder_email ? styles.inputError : ''}`}
          placeholder={t('calculator.forms.email_placeholder')}
          value={formData.policyholder?.email || ''}
          onChange={e => {
            setFormData(prev => ({
              ...prev,
              policyholder: {
                ...prev.policyholder,
                email: e.target.value,
              },
            }));
          }}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_email ? styles.errorHighlight : ''}`}
        >
          {validationErrors.policyholder_email || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.phone')}</label>
        <input
          className={`${styles.input} ${validationErrors.policyholder_phone_number ? styles.inputError : ''}`}
          type='tel'
          placeholder={t('calculator.forms.phone_placeholder')}
          value={formData.policyholder?.phone_number || ''}
          autoComplete='tel'
          onChange={e => {
            setFormData(prev => ({
              ...prev,
              policyholder: {
                ...prev.policyholder,
                phone_number: e.target.value,
              },
            }));
          }}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_phone_number ? styles.errorHighlight : ''}`}
        >
          {validationErrors.policyholder_phone_number || ''}
        </div>
      </div>

      {/* Policyholder Address – same API search as transport (settlements, streets, buildings) */}
      <div
        className={styles.formGroup}
        style={{ width: '100%', maxWidth: '100%', marginTop: '20px' }}
      >
        <label
          className={styles.label}
          style={{ fontSize: '18px', fontWeight: '500' }}
        >
          {t('calculator.forms.policyholder_address')}
        </label>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '20px',
          flexWrap: 'wrap',
          width: '100%',
        }}
      >
        <div
          className={styles.formGroup}
          style={{ flex: 1, minWidth: '200px' }}
        >
          <label className={styles.label}>
            {t('calculator.step6.municipality')}
          </label>
          <MunicipalityDropdown
            value={formData.policyholder?.municipality_code || ''}
            displayValue={formData.policyholder?.address?.municipality || ''}
            onChange={val => {
              if (!val) {
                setFormData(prev => ({
                  ...prev,
                  policyholder: {
                    ...prev.policyholder,
                    municipality_code: '',
                    settlement_code: '',
                    settlementHasStreets: undefined,
                    street_code: '',
                    building_code: '',
                    room_code: '',
                    address: {
                      ...prev.policyholder?.address,
                      municipality: '',
                      city: '',
                      street: '',
                      house_number: '',
                      flat_number: '',
                    },
                  },
                }));
                clearError('policyholder_municipality');
              }
            }}
            onMunicipalitySelect={municipality => {
              const municipalityName =
                municipality?.name || municipality?.name_normalized || '';
              const municipalityCode = municipality?.code || '';
              setFormData(prev => ({
                ...prev,
                policyholder: {
                  ...prev.policyholder,
                  municipality_code: municipalityCode,
                  settlement_code: '',
                  settlementHasStreets: undefined,
                  street_code: '',
                  building_code: '',
                  room_code: '',
                  address: {
                    ...prev.policyholder?.address,
                    municipality: municipalityName,
                    city: '',
                    street: '',
                    house_number: '',
                    flat_number: '',
                  },
                },
              }));
              clearError('policyholder_municipality');
              clearError('policyholder_city');
              clearError('policyholder_street');
              clearError('policyholder_house_number');
            }}
            placeholder={t('calculator.step6.placeholder_select_municipality')}
            error={!!validationErrors.policyholder_municipality}
            className={
              validationErrors.policyholder_municipality ? styles.inputError : ''
            }
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_municipality ? styles.errorHighlight : ''}`}
          >
            {validationErrors.policyholder_municipality || ''}
          </div>
        </div>
        <div
          className={styles.formGroup}
          style={{ flex: 1, minWidth: '200px' }}
        >
          <label className={styles.label}>{t('calculator.forms.city')}</label>
          <AddressSearchInput
            value={formData.policyholder?.settlement_code || ''}
            displayValue={formData.policyholder?.address?.city || ''}
            municipalityCode={formData.policyholder?.municipality_code || null}
            displayOnlyCityName={true}
            onChange={settlementCode => {
              setFormData(prev => ({
                ...prev,
                policyholder: {
                  ...prev.policyholder,
                  settlement_code: settlementCode,
                  ...(settlementCode === '' && {
                    settlementHasStreets: undefined,
                    street_code: '',
                    building_code: '',
                    room_code: '',
                    address: {
                      ...prev.policyholder?.address,
                      city: '',
                      street: '',
                      house_number: '',
                      flat_number: '',
                    },
                  }),
                },
              }));
              if (setValidationErrors && validationErrors.policyholder_city)
                clearError('policyholder_city');
              if (setValidationErrors && validationErrors.policyholder_street)
                clearError('policyholder_street');
              if (
                setValidationErrors &&
                validationErrors.policyholder_house_number
              )
                clearError('policyholder_house_number');
            }}
            onSettlementSelect={settlement => {
              setFormData(prev => ({
                ...prev,
                policyholder: {
                  ...prev.policyholder,
                  settlement_code: settlement.code,
                  municipality_code:
                    settlement.municipality_code ||
                    prev.policyholder?.municipality_code ||
                    '',
                  settlementHasStreets: undefined,
                  street_code: '',
                  building_code: '',
                  room_code: '',
                  address: {
                    ...prev.policyholder?.address,
                    municipality:
                      settlement.municipality_name ||
                      prev.policyholder?.address?.municipality ||
                      '',
                    city: settlement.name || '',
                    street: '',
                    house_number: '',
                    flat_number: '',
                  },
                },
              }));
              clearError('policyholder_municipality');
              clearError('policyholder_city');
              clearError('policyholder_street');
            }}
            placeholder={
              formData.policyholder?.municipality_code
                ? t('calculator.forms.city_placeholder')
                : t('calculator.forms.select_municipality_first')
            }
            error={!!validationErrors.policyholder_city}
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_city ? styles.errorHighlight : ''}`}
          >
            {validationErrors.policyholder_city || ''}
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '20px',
          flexWrap: 'wrap',
          width: '100%',
        }}
      >
        <div
          className={styles.formGroup}
          style={{ flex: 1, minWidth: '200px' }}
        >
          <label className={styles.label}>{t('calculator.forms.street')}</label>
          <StreetSearchInput
            value={formData.policyholder?.street_code || ''}
            displayValue={formData.policyholder?.address?.street || ''}
            settlementCode={formData.policyholder?.settlement_code || null}
            hasStreets={formData.policyholder?.settlementHasStreets}
            noStreetsPlaceholder={t('calculator.step6.placeholder_no_streets')}
            checkingPlaceholder={t('calculator.step6.placeholder_checking_streets')}
            onActivate={handlePolicyholderStreetCheck}
            onChange={streetCode => {
              setFormData(prev => ({
                ...prev,
                policyholder: {
                  ...prev.policyholder,
                  street_code: streetCode,
                  ...(streetCode === '' && {
                    building_code: '',
                    room_code: '',
                    address: {
                      ...prev.policyholder?.address,
                      street: '',
                      house_number: '',
                      flat_number: '',
                    },
                  }),
                },
              }));
              if (validationErrors.policyholder_street)
                clearError('policyholder_street');
              if (validationErrors.policyholder_house_number)
                clearError('policyholder_house_number');
            }}
            onQueryChange={text => {
              setFormData(prev => ({
                ...prev,
                policyholder: {
                  ...prev.policyholder,
                  street_code: '',
                  building_code: '',
                  address: {
                    ...prev.policyholder?.address,
                    street: text,
                  },
                },
              }));
            }}
            onUnmatched={() => {
              if (!setValidationErrors) return;
              setValidationErrors(prev => ({
                ...prev,
                policyholder_street: t(
                  'calculator.forms.validation.street_select_from_list'
                ),
              }));
            }}
            onStreetSelect={street => {
              const streetDisplay = street.street_type
                ? `${street.name || ''} ${street.street_type}`.trim()
                : street.name || '';
              setFormData(prev => ({
                ...prev,
                policyholder: {
                  ...prev.policyholder,
                  street_code: street.code,
                  building_code: '',
                  room_code: '',
                  address: {
                    ...prev.policyholder?.address,
                    street: streetDisplay,
                    house_number: '',
                    flat_number: '',
                  },
                },
              }));
              clearError('policyholder_street');
            }}
            placeholder={
              formData.policyholder?.settlement_code
                ? t('calculator.forms.street_placeholder')
                : t('calculator.step6.placeholder_select_city_first')
            }
            error={!!validationErrors.policyholder_street}
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_street ? styles.errorHighlight : ''}`}
          >
            {validationErrors.policyholder_street || ''}
          </div>
        </div>
        <div
          className={styles.formGroup}
          style={{ flex: 1, minWidth: '200px' }}
        >
          <label className={styles.label}>{t('calculator.forms.house_number')}</label>
          <BuildingSearchInput
            value={formData.policyholder?.building_code || ''}
            displayValue={formData.policyholder?.address?.house_number || ''}
            streetCode={
              formData.policyholder?.settlementHasStreets === false
                ? null
                : formData.policyholder?.street_code || null
            }
            onActivate={handlePolicyholderStreetCheck}
            onChange={buildingCode => {
              setFormData(prev => ({
                ...prev,
                policyholder: {
                  ...prev.policyholder,
                  building_code: buildingCode,
                  ...(buildingCode === '' && {
                    room_code: '',
                    address: {
                      ...prev.policyholder?.address,
                      house_number: '',
                      flat_number: '',
                    },
                  }),
                },
              }));
              if (validationErrors.policyholder_house_number)
                clearError('policyholder_house_number');
            }}
            onBuildingSelect={building => {
              setFormData(prev => ({
                ...prev,
                policyholder: {
                  ...prev.policyholder,
                  building_code: building.code,
                  room_code: '',
                  address: {
                    ...prev.policyholder?.address,
                    house_number:
                      building.full_number || building.building_number || '',
                    flat_number: '',
                  },
                },
              }));
              clearError('policyholder_house_number');
            }}
            placeholder={t('calculator.forms.house_placeholder')}
            error={!!validationErrors.policyholder_house_number}
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_house_number ? styles.errorHighlight : ''}`}
          >
            {validationErrors.policyholder_house_number || ''}
          </div>
        </div>
        <div
          className={styles.formGroup}
          style={{ flex: 1, minWidth: '200px' }}
        >
          <label className={styles.label}>{t('calculator.forms.apartment_number')}</label>
          <RoomSearchInput
            value={formData.policyholder?.room_code || ''}
            displayValue={formData.policyholder?.address?.flat_number || ''}
            buildingCode={formData.policyholder?.building_code || null}
            onChange={roomCode => {
              setFormData(prev => ({
                ...prev,
                policyholder: {
                  ...prev.policyholder,
                  room_code: roomCode,
                  ...(roomCode === '' && {
                    address: {
                      ...prev.policyholder?.address,
                      flat_number: '',
                    },
                  }),
                },
              }));
              if (validationErrors.policyholder_flat_number)
                clearError('policyholder_flat_number');
            }}
            onRoomSelect={room => {
              setFormData(prev => ({
                ...prev,
                policyholder: {
                  ...prev.policyholder,
                  room_code: room.code,
                  address: {
                    ...prev.policyholder?.address,
                    flat_number: room.room_number || '',
                  },
                },
              }));
              clearError('policyholder_flat_number');
            }}
            placeholder={
              formData.policyholder?.building_code
                ? t('calculator.forms.apartment_placeholder')
                : t('calculator.step6.placeholder_select_building_first')
            }
            error={!!validationErrors.policyholder_flat_number}
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_flat_number ? styles.errorHighlight : ''}`}
          >
            {validationErrors.policyholder_flat_number || ''}
          </div>
        </div>
      </div>

      {/* Draudimo pradžios data is set on step 1 – not shown here */}
        </>
      ) : null}

      {showMachineryIdentity ? (
        <>
      {/* Machinery Section */}
      <div
        className={styles.formGroup}
        style={{
          width: '100%',
          maxWidth: '100%',
          marginTop: fieldSet === 'machineryIdentity' ? 0 : '24px',
        }}
      >
        <label
          className={styles.label}
          style={{ fontSize: '18px', fontWeight: '500' }}
        >
          {t('calculator.forms.cpm_machinery_heading')}
        </label>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.cpm_make')}</label>
        <input
          className={`${styles.input} ${validationErrors.cpm_make ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.cpm_make_placeholder')}
          value={formData.cpm_make || ''}
          onChange={e => {
            setFormData(prev => ({ ...prev, cpm_make: e.target.value }));
            clearError('cpm_make');
          }}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.cpm_make ? styles.errorHighlight : ''}`}
        >
          {validationErrors.cpm_make || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.cpm_model')}</label>
        <input
          className={`${styles.input} ${validationErrors.cpm_model ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.cpm_model_placeholder')}
          value={formData.cpm_model || ''}
          onChange={e => {
            setFormData(prev => ({ ...prev, cpm_model: e.target.value }));
            clearError('cpm_model');
          }}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.cpm_model ? styles.errorHighlight : ''}`}
        >
          {validationErrors.cpm_model || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.field_cpm_category')}</label>
        <Select
          options={categoryOptions}
          value={category}
          onChange={value => {
            setFormData(prev => ({
              ...prev,
              cpm_category: value,
              cpm_subcategory: '',
            }));
            clearError('cpm_category');
          }}
          placeholder={t('calculator.forms.cpm_category_placeholder')}
          className={validationErrors.cpm_category ? styles.selectError : ''}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.cpm_category ? styles.errorHighlight : ''}`}
        >
          {validationErrors.cpm_category || ''}
        </div>
      </div>

      {category && (
        <div className={styles.formGroup}>
          <label className={styles.label}>{t('calculator.forms.field_cpm_subcategory')}</label>
          <Select
            options={subcategoryOptions}
            value={formData.cpm_subcategory || ''}
            onChange={value => {
              setFormData(prev => ({ ...prev, cpm_subcategory: value }));
              clearError('cpm_subcategory');
            }}
            placeholder={t('calculator.forms.cpm_subcategory_placeholder')}
            className={
              validationErrors.cpm_subcategory ? styles.selectError : ''
            }
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && validationErrors.cpm_subcategory ? styles.errorHighlight : ''}`}
          >
            {validationErrors.cpm_subcategory || ''}
          </div>
        </div>
      )}

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.cpm_vin')}</label>
        <input
          className={`${styles.input} ${validationErrors.cpm_vin ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.cpm_vin_placeholder')}
          value={formData.cpm_vin || ''}
          maxLength={24}
          onChange={e => {
            setFormData(prev => ({ ...prev, cpm_vin: e.target.value }));
            clearError('cpm_vin');
          }}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.cpm_vin ? styles.errorHighlight : ''}`}
        >
          {validationErrors.cpm_vin || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.cpm_year')}</label>
        <input
          className={`${styles.input} ${validationErrors.cpm_manufacture_year ? styles.inputError : ''}`}
          type='number'
          min={1900}
          max={2100}
          placeholder={t('calculator.forms.cpm_year_placeholder')}
          value={formData.cpm_manufacture_year ?? ''}
          onChange={e => {
            const v = e.target.value === '' ? '' : e.target.value;
            setFormData(prev => ({ ...prev, cpm_manufacture_year: v }));
            clearError('cpm_manufacture_year');
          }}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.cpm_manufacture_year ? styles.errorHighlight : ''}`}
        >
          {validationErrors.cpm_manufacture_year || ''}
        </div>
      </div>
        </>
      ) : null}

      {showInsuranceFields ? (
        <>
      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.stepper_info.insured_sum_eur')}</label>
        <input
          className={`${styles.input} ${validationErrors.cpm_sum_insured ? styles.inputError : ''}`}
          type='text'
          inputMode='decimal'
          placeholder={t('calculator.forms.cpm_sum_placeholder')}
          value={formData.cpm_sum_insured ?? ''}
          onChange={e => {
            const v = e.target.value.replace(',', '.');
            setFormData(prev => ({ ...prev, cpm_sum_insured: v }));
            clearError('cpm_sum_insured');
          }}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.cpm_sum_insured ? styles.errorHighlight : ''}`}
        >
          {validationErrors.cpm_sum_insured || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.stepper_info.deductible_franchise')}</label>
        <Select
          options={deductibleOptions}
          value={formData.cpm_deductible || ''}
          onChange={value => {
            setFormData(prev => ({ ...prev, cpm_deductible: value }));
            clearError('cpm_deductible');
          }}
          placeholder={t('calculator.step6.select_deductible')}
          className={validationErrors.cpm_deductible ? styles.selectError : ''}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.cpm_deductible ? styles.errorHighlight : ''}`}
        >
          {validationErrors.cpm_deductible || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <div className={styles.labelWithInfo}>
          <label className={styles.label}>{t('calculator.stepper_info.insurance_package')}</label>
          <div
            className={styles.fieldInfoIconWrap}
            onMouseEnter={() => setCpmPackageTooltipOpen(true)}
            onMouseLeave={() => setCpmPackageTooltipOpen(false)}
          >
            <button
              type='button'
              className={styles.packageInfoButton}
              aria-label={t('calculator.forms.cpm_package_info_aria')}
              onFocus={() => setCpmPackageTooltipOpen(true)}
              onBlur={() => setCpmPackageTooltipOpen(false)}
            >
              <IconInfo width={18} height={18} color='currentColor' />
            </button>
            {cpmPackageTooltipOpen && (
              <div
                className={`${styles.fieldInfoTooltip} ${styles.fieldInfoTooltipWide}`}
                role='tooltip'
              >
                <strong>{t('calculator.stepper_info.cpm_package.CLASSIC')}</strong>
                {' – '}
                {t('calculator.forms.cpm_package_classic_body')}
                <br />
                <br />
                <strong>{t('calculator.stepper_info.cpm_package.MAXIMUM')}</strong>
                {' – '}
                {t('calculator.forms.cpm_package_maximum_body')}
              </div>
            )}
          </div>
        </div>
        <Select
          options={packageOptions}
          value={formData.cpm_package || ''}
          onChange={value => {
            setFormData(prev => ({ ...prev, cpm_package: value }));
            clearError('cpm_package');
          }}
          placeholder={t('calculator.forms.cpm_package_placeholder')}
          className={validationErrors.cpm_package ? styles.selectError : ''}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.cpm_package ? styles.errorHighlight : ''}`}
        >
          {validationErrors.cpm_package || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.stepper_info.rent_type')}</label>
        <Select
          options={rentOptions}
          value={formData.cpm_rent_type || ''}
          onChange={value => {
            setFormData(prev => ({ ...prev, cpm_rent_type: value }));
            clearError('cpm_rent_type');
          }}
          placeholder={t('calculator.forms.cpm_rent_placeholder')}
          className={validationErrors.cpm_rent_type ? styles.selectError : ''}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.cpm_rent_type ? styles.errorHighlight : ''}`}
        >
          {validationErrors.cpm_rent_type || ''}
        </div>
      </div>

      {!hidePolicyholder ||
      (formData.cpm_vehicle_input_mode || 'plate') !== 'plate' ? (
      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.stepper_info.license_plate')}</label>
        <input
          className={styles.input}
          type='text'
          placeholder={t('calculator.forms.cpm_plate_placeholder')}
          value={formData.cpm_vehicle_license_plate || ''}
          onChange={e =>
            setFormData(prev => ({
              ...prev,
              cpm_vehicle_license_plate: e.target.value,
            }))
          }
        />
      </div>
      ) : null}

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.stepper_info.depreciation')}</label>
        <Select
          options={depreciationOptions}
          value={formData.cpm_depreciation || ''}
          onChange={value =>
            setFormData(prev => ({ ...prev, cpm_depreciation: value }))
          }
          placeholder={t('calculator.step6.select_option')}
        />
      </div>

      {/* File upload */}
      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.stepper_info.attachments_documents')}</label>
        <input
          className={styles.input}
          type='file'
          multiple
          accept='.pdf,.zip,.jpg,.jpeg,.png'
          onChange={e => {
            const files = e.target.files ? Array.from(e.target.files) : [];
            handleFileChange('cpm_files', files);
          }}
        />
        {formData.cpm_files?.length > 0 && (
          <div className={styles.fieldError} style={{ marginTop: 4 }}>
            {t('calculator.stepper_info.files_selected').replace('{count}', String(formData.cpm_files.length))}
          </div>
        )}
      </div>
        </>
      ) : null}

    </>
  );
}
